import { addDays } from './dates.js';

const CODE_TO_DOW: Record<string, number> = {
  SU: 0, MO: 1, TU: 2, WE: 3, TH: 4, FR: 5, SA: 6,
};

// Returns the first occurrence of `rrule` on or after `today` (YYYY-MM-DD).
// Handles the patterns the quick-add parser emits; falls back to `today` for
// anything it doesn't recognise.
export function firstOccurrence(rrule: string, today: string): string {
  const parts = Object.fromEntries(
    rrule.split(';').map((kv) => {
      const [k, v] = kv.split('=');
      return [k ?? '', v ?? ''];
    })
  );
  const freq = parts['FREQ'];
  const byday = parts['BYDAY'];
  const bymonth = parts['BYMONTH'];
  const bymonthday = parts['BYMONTHDAY'];

  const [ty, tm, td] = today.split('-').map(Number) as [number, number, number];

  if (freq === 'WEEKLY' && byday) {
    const targets = byday.split(',').map((c) => CODE_TO_DOW[c]).filter((n): n is number => n !== undefined);
    if (targets.length === 0) return today;
    const current = new Date(Date.UTC(ty, tm - 1, td)).getUTCDay();
    let bestDelta = 7;
    for (const t of targets) {
      const delta = (t - current + 7) % 7;
      if (delta < bestDelta) bestDelta = delta;
    }
    return addDays(today, bestDelta);
  }

  if (freq === 'MONTHLY' && bymonthday && !bymonth) {
    const day = parseInt(bymonthday, 10);
    return nextMonthlyDay(ty, tm, td, day);
  }

  if (freq === 'MONTHLY' && byday) {
    const m = byday.match(/^(-?\d+)([A-Z]{2})$/);
    if (m) {
      const n = parseInt(m[1]!, 10);
      const dow = CODE_TO_DOW[m[2]!];
      if (dow !== undefined) {
        const iso = nthWeekdayOfMonth(ty, tm, n, dow);
        if (iso >= today) return iso;
        const next = tm === 12 ? [ty + 1, 1] : [ty, tm + 1];
        return nthWeekdayOfMonth(next[0]!, next[1]!, n, dow);
      }
    }
  }

  if (freq === 'YEARLY' && bymonth && bymonthday) {
    const month = parseInt(bymonth, 10);
    const day = parseInt(bymonthday, 10);
    const thisYear = iso(ty, month, day);
    if (thisYear >= today) return thisYear;
    return iso(ty + 1, month, day);
  }

  return today;
}

function nextMonthlyDay(ty: number, tm: number, td: number, day: number): string {
  if (day >= td) {
    const candidate = iso(ty, tm, day);
    if (candidate !== '') return candidate;
  }
  const [ny, nm] = tm === 12 ? [ty + 1, 1] : [ty, tm + 1];
  return iso(ny, nm, day);
}

function nthWeekdayOfMonth(y: number, m: number, n: number, dow: number): string {
  if (n === -1) {
    const last = new Date(Date.UTC(y, m, 0));
    const lastDow = last.getUTCDay();
    const delta = (lastDow - dow + 7) % 7;
    return iso(y, m, last.getUTCDate() - delta);
  }
  const first = new Date(Date.UTC(y, m - 1, 1)).getUTCDay();
  const offset = (dow - first + 7) % 7;
  return iso(y, m, 1 + offset + (n - 1) * 7);
}

function iso(y: number, m: number, d: number): string {
  return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}
